import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * First-visit welcome popup.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends {DialogComponent}
 */
class WelcomePopupComponent extends DialogComponent {
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    const storageKey = this.getAttribute('storage-key') || 'welcome-popup-dismissed';
    const isDesignMode = this.hasAttribute('design-mode');
    const hasFeedback = Boolean(this.querySelector('[role="status"], [role="alert"]'));
    const delayMs = hasFeedback ? 0 : Number(this.getAttribute('delay') || 0) * 1000;

    if (!isDesignMode) {
      this.addEventListener(DialogCloseEvent.eventName, () => {
        this.#persistDismissed();
      });

      const form = this.querySelector('form');
      form?.addEventListener('submit', () => {
        this.#persistDismissed();
      });

      try {
        if (localStorage.getItem(storageKey)) return;
      } catch {
        // If storage is unavailable, still show the popup once this session.
      }
    }

    this.#whenAgeGateCleared(() => {
      window.setTimeout(() => {
        this.showDialog();
      }, isDesignMode ? 0 : delayMs);
    });
  }

  /**
   * Open as a non-modal dialog so it stays inside the flex overlay for centering.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.show();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.style.animation = 'none';
    void this.offsetWidth;
    dialog.classList.add('dialog-closing');
    this.style.animation = '';

    await onAnimationEnd(this, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Wait until the age gate is dismissed so this modal cannot stack above it.
   *
   * @param {() => void} callback
   */
  #whenAgeGateCleared(callback) {
    if (this.hasAttribute('design-mode')) {
      callback();
      return;
    }

    const ageGate = document.querySelector('age-gate-component');
    if (!ageGate) {
      callback();
      return;
    }

    const isCleared = () => {
      if (document.documentElement.hasAttribute('data-age-verified')) return true;
      if (ageGate.hasAttribute('hidden')) return true;

      try {
        return localStorage.getItem(ageGate.getAttribute('storage-key') || 'age-gate-verified') === 'true';
      } catch {
        return false;
      }
    };

    if (isCleared()) {
      callback();
      return;
    }

    const observer = new MutationObserver(() => {
      if (isCleared()) {
        observer.disconnect();
        callback();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-age-verified'],
    });
    observer.observe(ageGate, {
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  #persistDismissed() {
    const storageKey = this.getAttribute('storage-key') || 'welcome-popup-dismissed';

    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      // Ignore storage failures (private mode, disabled storage, etc.)
    }
  }
}

if (!customElements.get('welcome-popup-component')) {
  customElements.define('welcome-popup-component', WelcomePopupComponent);
}
